/**
 * SHER-210 — auto-calculated IMC (BMI) field.
 *
 * Cardio24's nursing constants form already has separate Poids / Taille / IMC
 * fields. Rather than a bundled widget that duplicates weight/height inputs, the
 * `imc` field type derives its value from the sibling weight and height fields
 * already on the form. It identifies them by name convention (the form engine has
 * no field-dependency config), matching the field labels against multilingual
 * keyword lists.
 */

const WEIGHT_KEYWORDS = ['poids', 'peso', 'weight'];
const HEIGHT_KEYWORDS = ['taille', 'talla', 'height', 'altura', 'estatura'];

interface LabelledValue {
    label?: any;
    value?: any;
}
export interface WeightHeight {
    weight: number | null;
    height: number | null;
}

function normalize(text: any): string {
    return (text == null ? '' : String(text))
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}

function toNum(value: any): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/**
 * Scans the form's fields (id -> {label, value}) for the weight and height
 * fields by label-keyword convention. Returns the first match in each category.
 */
export function findWeightHeight(
    formValues: Record<string, LabelledValue> | undefined | null,
): WeightHeight {
    let weight: number | null = null;
    let height: number | null = null;
    for (const key of Object.keys(formValues || {})) {
        const entry = formValues![key];
        if (!entry) {
            continue;
        }
        const haystack = normalize(entry.label);
        if (weight === null && WEIGHT_KEYWORDS.some((k) => haystack.includes(k))) {
            weight = toNum(entry.value);
        } else if (height === null && HEIGHT_KEYWORDS.some((k) => haystack.includes(k))) {
            height = toNum(entry.value);
        }
    }
    return { weight, height };
}

/**
 * BMI = weight(kg) / height(m)^2, rounded to 2 decimals. Returns null when
 * either input is missing or non-positive.
 */
export function computeImc(weight: any, height: any): number | null {
    const w = toNum(weight);
    const h = toNum(height);
    if (w === null || h === null || w <= 0 || h <= 0) {
        return null;
    }
    const heightMetres = h / 100;
    const imc = w / (heightMetres * heightMetres);
    if (!Number.isFinite(imc)) {
        return null;
    }
    return Math.round(imc * 100) / 100;
}
